<template>
  <div class="order-history">
    <div v-if="isLoading" class="text-center py-5">
      <div class="spinner-border spinner-border-sm text-dark" role="status"></div>
    </div>

    <div v-else-if="orders.length === 0" class="text-center py-5 bg-light border border-dashed">
      <p class="mb-0 text-muted small text-uppercase letter-spacing-1">{{ t('account.orders.no_orders') }}</p>
    </div>

    <div v-else class="orders-list">
      <div v-for="order in orders" :key="order.orderId" class="order-item p-4 mb-3 border bg-white">
        <div class="row align-items-center py-1">
          <!-- Colonna Numero Ordine -->
          <div class="col-lg-2">
            <span class="info-label d-block mb-1">{{ t('account.orders.order_num') }}</span>
            <span class="info-value fw-bold letter-spacing-05">{{ order.orderNumber }}</span>
            <span v-if="order.customerId" class="text-muted" style="font-size: 10px;">ID Cliente: {{ order.customerId }}</span>
          </div>
          
          <!-- Colonna Data -->
          <div class="col-lg-2">
            <span class="info-label d-block mb-1">{{ t('account.orders.date') }}</span>
            <span class="info-value">{{ formatDate(order.orderDate) }}</span>
          </div>

          <!-- Colonna Totale -->
          <div class="col-lg-2">
            <span class="info-label d-block mb-1">{{ t('account.orders.total') }}</span>
            <span class="info-value fw-bold">{{ formatPrice(order.totalAmount) }}</span>
            <span class="text-muted" style="font-size: 11px;">{{ order.totalQuantity || 0 }} {{ order.totalQuantity === 1 ? 'articolo' : 'articoli' }}</span>
          </div>

          <!-- Colonna Stato -->
          <div class="col-lg-3 pe-lg-3">
            <span class="info-label d-block mb-1">{{ t('account.orders.status') }}</span>
            <span class="status-badge mb-2" :class="(order.status || '').toLowerCase()">
              {{ formatStatus(order.status) }}
            </span>
            
            <!-- Miniature Prodotti -->
            <div v-if="order.productThumbnails && order.productThumbnails.length > 0" class="d-flex gap-2 flex-wrap">
              <img 
                v-for="(thumb, idx) in order.productThumbnails.slice(0, 5)" 
                :key="idx" 
                :src="thumb.startsWith('http') ? thumb : '/images/products/' + thumb" 
                class="order-thumbnail"
                alt="Prodotto"
              >
            </div>
          </div>

          <!-- Colonna Azioni -->
          <div class="col-lg-3 text-lg-end mt-3 mt-lg-0 ps-lg-4">
            <div class="d-flex flex-column gap-2 ms-lg-auto action-container">
              <button 
                v-if="(order.status || '').toLowerCase() === 'pendingpayment'" 
                @click="goToPayment(order)"
                class="btn btn-dark btn-action"
              >
                {{ t('account.orders.pay_now') }}
              </button>
              <button 
                @click="goToDetails(order)"
                class="btn btn-outline-dark btn-action"
              >
                {{ t('account.orders.details') }}
              </button>
              <button 
                v-if="(order.status || '').toLowerCase() === 'pendingpayment'" 
                @click="handleDelete(order)"
                class="btn btn-link btn-delete mt-1"
              >
                {{ t('account.orders.delete') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { salesOrderService } from '@/services/salesOrderService'
import { useLanguage } from '@/composables/useLanguage'
import { useToast } from '@/composables/useToast'

const router = useRouter()
const toast = useToast()
const { t } = useLanguage()
const orders = ref([])
const isLoading = ref(true)

const formatPrice = (price) => {
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR'
  }).format(price)
}

const formatDate = (dateString) => {
  if (!dateString) return '-'
  return new Date(dateString).toLocaleDateString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  })
}

const formatStatus = (status) => {
  const statuses = {
    'pendingpayment': 'In attesa di pagamento',
    'pending': 'In attesa di pagamento',
    'paid': 'Pagato',
    'processing': 'In elaborazione',
    'paymentfailed': 'Pagamento fallito',
    'shipped': 'Spedito',
    'delivered': 'Consegnato',
    'cancelled': 'Annullato',
    'completed': 'Completato'
  }
  const statusKey = (status || '').toLowerCase()
  return statuses[statusKey] || status
}

const fetchOrders = async () => {
  try {
    isLoading.value = true
    const response = await salesOrderService.getMyOrders()
    
    // Parsing ultra-resiliente basato sulle specifiche SalesOrderDto
    const rawOrders = response.items || response.data || (Array.isArray(response) ? response : [])
    
    orders.value = rawOrders
      .filter(o => o !== null && typeof o === 'object')
      .map(o => ({
        orderId: o.salesOrderId ?? o.OrderId ?? o.id,
        customerId: o.customerId ?? o.CustomerId,
        orderNumber: o.orderNumber ?? o.OrderNumber ?? 'N/A',
        orderDate: o.orderDate ?? o.OrderDate ?? o.createdAt,
        totalAmount: o.orderTotal ?? o.TotalAmount ?? o.totalAmount ?? o.total ?? 0,
        totalQuantity: o.totalQuantity,
        productThumbnails: o.productThumbnails || [],
        status: String(o.orderStatus ?? o.status ?? o.Status ?? 'PendingPayment')
      }))
      .sort((a, b) => {
        const dateA = new Date(a.orderDate || 0)
        const dateB = new Date(b.orderDate || 0)
        return dateB - dateA
      })
      
  } catch (error) {
    console.error('[OrderHistory] Error:', error)
  } finally {
    isLoading.value = false
  }
}

const goToPayment = (order) => {
  router.push({
    name: 'Payment',
    query: {
      orderId: order.orderId,
      total: order.totalAmount,
      orderNumber: order.orderNumber
    }
  })
}

const goToDetails = (order) => {
  router.push({
    name: 'OrderDetail',
    params: { id: order.orderId }
  })
}

const handleDelete = async (order) => {
  try {
    await salesOrderService.deleteOrder(order.orderId)
    toast.success(t('account.orders.delete_success'))
    // Refresh della lista
    await fetchOrders()
  } catch (error) {
    toast.error(t('toast.error.generic'))
  }
}

onMounted(fetchOrders)
</script>

<style scoped>
.order-item {
  border-color: #eee !important;
  transition: all 0.2s ease-in-out;
}

.order-item:hover {
  border-color: #000 !important;
  box-shadow: 0 4px 12px rgba(0,0,0,0.03);
}

.info-label {
  font-size: 9px;
  font-weight: 700;
  color: #bbb;
  text-transform: uppercase;
  letter-spacing: 1.2px;
}

.info-value {
  font-size: 13px;
  color: #111;
  display: block;
}

.letter-spacing-05 {
  letter-spacing: 0.5px;
}

.status-badge {
  display: inline-block;
  font-size: 9px;
  font-weight: 800;
  text-transform: uppercase;
  padding: 5px 10px;
  letter-spacing: 0.8px;
  border-radius: 0;
}

.status-badge.pendingpayment {
  background: #fff9e6;
  color: #b08905;
}

.status-badge.processing {
  background: #e0f7fa;
  color: #006064;
}

.status-badge.paymentfailed {
  background: #fff5f5;
  color: #c0392b;
}

.status-badge.cancelled {
  background: #f8f9fa;
  color: #6c757d;
}

.status-badge.paid {
  background: #e6fcf5;
  color: #087f5b;
}

.order-thumbnail {
  width: 50px;
  height: 50px;
  object-fit: cover;
  border-radius: 4px;
  border: 1px solid #eee;
}

.action-container {
  max-width: 140px;
}

.btn-action {
  border-radius: 0;
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  padding: 10px;
  width: 100%;
}

.btn-outline-dark {
  border-color: #eee;
  color: #666;
}

.btn-outline-dark:hover {
  border-color: #000 !important;
  background: #000;
  color: #fff;
}

.btn-delete {
  font-size: 10px;
  font-weight: 700;
  color: #dc3545;
  text-decoration: none;
  text-transform: uppercase;
  letter-spacing: 1px;
  padding: 0;
  transition: opacity 0.2s;
}

.btn-delete:hover {
  color: #a71d2a;
  opacity: 0.8;
}

.border-dashed {
  border: 1px dashed #ccc !important;
}

@media (max-width: 991px) {
  .row > div {
    margin-bottom: 15px;
  }
  .row > div:last-child {
    margin-bottom: 0;
  }
  .action-container {
    max-width: 100% !important;
    flex-direction: row !important;
  }
}
</style>
